import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CheckboxComponent, InputComponent } from '@lib/ui';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { FormSchema } from './form.schema';
import { CreateDataPrivacyAgreementUserRequest, handleResponse, usePostApiDataPrivacySiteSignupMutation } from '@lib/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface IProps {
  onSuccessfulSubmit: () => void;
}

type FormData = CreateDataPrivacyAgreementUserRequest & {
  agreed: boolean;
};

export function DataPrivacyFormComponent(props: IProps) {
  const { t } = useTranslation('data_privacy');
  const [postForm, { isLoading: isLoadingForm }] = usePostApiDataPrivacySiteSignupMutation();

  const form = useForm<FormData>({
    defaultValues: {
      email: '',
      firstName: '',
      surname: '',
      street: '',
      number: '',
      city: '',
      zipCode: '',
    },
    resolver: yupResolver(FormSchema),
    mode: 'all',
  });

  const {
    handleSubmit,
    register,
    formState: { isValid },
  } = form;

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const response = await postForm({
      body: {
        email: data.email,
        firstName: data.firstName,
        surname: data.surname,
        street: data.street,
        number: data.number,
        city: data.city,
        zipCode: data.zipCode,
      },
    });

    handleResponse(
      response,
      (error) => {
        if (error.status === 409) {
          toast.error(t('toast_failure.used_email'));
        } else {
          toast.error(t('toast_failure.error'));
        }
      },
      () => {
        if (props.onSuccessfulSubmit) props.onSuccessfulSubmit();
        toast.success(t('toast_success'));
      },
    );
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputComponent name="email" label={t('email', { ns: 'auth' })} type="email" required className="mt-4 mb-4" />

        <div className="md:flex md:space-x-4">
          <InputComponent name="firstName" label={t('firstname', { ns: 'auth' })} className="flex-1 mb-4" required />
          <InputComponent name="surname" label={t('lastname', { ns: 'auth' })} className="flex-1 mb-4" required />
        </div>

        <div className="md:flex md:space-x-4">
          <InputComponent name="street" label={t('address.street', { ns: 'common' })} className="flex-1 mb-4" required />
          <InputComponent name="number" label={t('address.street_number', { ns: 'common' })} className="flex-1 mb-4" required />
        </div>

        <div className="md:flex md:space-x-4">
          <InputComponent
            name="zipCode"
            type="number"
            step="any"
            min={0}
            inputMode="numeric"
            label={t('address.zipcode', { ns: 'common' })}
            className="flex-1 mb-4"
            required
          />
          <InputComponent name="city" label={t('location')} className="flex-1 mb-4" required />
        </div>

        <CheckboxComponent {...register('agreed')} label={t('consent_message')} wrapperClassName="mb-4 items-start!" required />

        <p className="text-xs mb-4">{t('revoke')}</p>

        <Button size="M" disabled={isLoadingForm || !isValid}>
          {t('submit')}
        </Button>
      </form>
    </FormProvider>
  );
}

export default DataPrivacyFormComponent;
